import { useEffect, useState } from 'react'
import { Alert, Badge, Button, FormSelect } from '../ui'

function availableTimezones() {
  const supported = typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : ['UTC', 'Europe/Copenhagen', 'Europe/London', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles']
  return [...new Set(['UTC', ...supported])]
}

export default function TimezoneSetupStep({ step, onApply, onRefresh, onNext }) {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  const [editing, setEditing] = useState(step?.status !== 'complete')
  const [timezone, setTimezone] = useState(step?.value || browserTimezone)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (step?.value) setTimezone(step.value)
  }, [step?.value])

  const apply = async () => {
    setSaving(true)
    setError(null)
    try {
      await onApply(timezone)
      setEditing(false)
    } catch (requestError) {
      if (requestError.response?.status === 409) await onRefresh()
      setError('We couldn\'t save the timezone. Try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-[var(--app-text)]">Set the workspace timezone</h2>
          <p className="mt-1 text-sm text-[var(--app-text-muted)]">
            Dates, schedules, and activity times will use this timezone.
          </p>
        </div>
        {step?.status === 'complete' && <Badge color="info">Complete</Badge>}
      </div>

      {error && <Alert color="danger">{error}</Alert>}

      {editing ? (
        <label className="block space-y-2 text-sm font-medium text-[var(--app-text)]">
          Workspace timezone
          <FormSelect value={timezone} onChange={(event) => setTimezone(event.target.value)}>
            {availableTimezones().map((value) => <option key={value} value={value}>{value}</option>)}
          </FormSelect>
        </label>
      ) : (
        <div className="rounded-[var(--app-radius)] border border-[var(--app-border)] bg-[var(--app-surface-2)] p-3">
          <div className="text-xs text-[var(--app-text-muted)]">Workspace timezone</div>
          <div className="mt-1 font-medium text-[var(--app-text)]">{step?.value}</div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {step?.status === 'complete' && !editing && <Button onClick={() => setEditing(true)}>Edit</Button>}
        {editing ? (
          <Button color="primary" disabled={saving} onClick={apply}>
            {saving ? 'Applying…' : 'Apply timezone'}
          </Button>
        ) : (
          <Button color="primary" onClick={onNext}>Next</Button>
        )}
      </div>
    </div>
  )
}
